// Vendors
import { useState, useRef, useEffect } from 'react';

// Components
import { Icon } from 'texkit/dist/components/Icon/Icon';

// Styles
import './accessibility-select.scss';

// AccessibilitySelect turns the dropdown options to html buttons so accessibility tools (e.g. Silktide) and other screen readers can discover and read out the text content to the user.
const AccessibilitySelect = ({
  name,
  options = [],
  label,
  formikProps,
  emptyOptionLabel,
  placeholder,
  disabled,
  disableTyping = false,
}) => {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const ref = useRef(null);
  const controlRef = useRef(null);
  const ignoreCloseRef = useRef(false);
  const userHasTypedRef = useRef(false);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const optionRefs = useRef([]);

  const filteredOptions = filterText
    ? options.filter(opt => {
        const optLabel =
          opt && (opt.question || opt.label)
            ? opt.question || opt.label
            : String(opt);
        return optLabel.toLowerCase().includes(filterText.toLowerCase());
      })
    : options;

  useEffect(() => {
    if (open && focusedIndex !== null && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex].focus();
    }
  }, [open, focusedIndex]);

  useEffect(() => {
    setFocusedIndex(null);
  }, [filterText]);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setFilterText('');
      }
    }

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fieldValue =
    formikProps && formikProps.getFieldValue
      ? formikProps.getFieldValue(name)
      : formikProps && formikProps.values
      ? formikProps.values[name]
      : null;

  const selected =
    fieldValue && typeof fieldValue === "object" ? fieldValue : null;
  const selectedLabel = selected
    ? selected.question || selected.label || String(selected)
    : '';
  const displayLabel = selectedLabel || emptyOptionLabel || "Select...";

  const handleSelect = (option, e, submit = false) => {
    if (formikProps && formikProps.setFieldValue) {
      formikProps.setFieldValue(name, option);
    }
    if (formikProps && formikProps.setFieldTouched) {
      formikProps.setFieldTouched(name, true);
    }
    if (submit && formikProps && typeof formikProps.submitForm === "function") {
      formikProps.submitForm();
    }
    setFilterText('');
    setOpen(false);
    setTimeout(() => {
      if (
        controlRef.current &&
        typeof controlRef.current.focus === "function"
      ) {
        ignoreCloseRef.current = true;
        controlRef.current.focus();
      }
    }, 0);
  };

  const openDropdown = () => {
    setFilterText('');
    userHasTypedRef.current = false;
    setFocusedIndex(null);
    setOpen(true);
  };

  return (
    <div
      className={`accessibility-select input-select input-wrapper hasLabel input-label-position-above ${
        disabled ? 'accessibility-select--disabled' : ''
      }`}
      ref={ref}
    >
      <div className="input-label">{label}</div>
      <div className="input-wrapper-field">
        {!open && !selectedLabel && emptyOptionLabel != null && (
          <span className="input-select__empty-label" aria-hidden="true">
            {emptyOptionLabel}
          </span>
        )}
        <input
          type="text"
          ref={controlRef}
          className="input input-select__control"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
          value={disableTyping ? (selectedLabel || '') : (open ? (userHasTypedRef.current ? filterText : (filterText || selectedLabel)) : selectedLabel)}
          placeholder={placeholder || ''}
          readOnly={disableTyping}
          onChange={(e) => {
            if (disableTyping) return;
            userHasTypedRef.current = true;
            setFilterText(e.target.value);
            if (!open) {
              ignoreCloseRef.current = true;
              setOpen(true);
            }
          }}
          onFocus={() => {
            if (ignoreCloseRef.current) {
              ignoreCloseRef.current = false;
              return;
            }
            openDropdown();
          }}
          onMouseDown={(e) => {
            openDropdown();
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
              setFilterText('');
            }
            if (e.key === "Enter" || e.key === " ") {
              if (!open) {
                e.preventDefault();
                e.stopPropagation();
                openDropdown();
                setFocusedIndex(0);
              }
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              e.stopPropagation();
              if (!open) {
                openDropdown();
                setFocusedIndex(0);
              } else {
                setFocusedIndex((prev) => {
                  const next =
                    prev === null ? 0 : Math.min(prev + 1, filteredOptions.length - 1);
                  return next;
                });
              }
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              e.stopPropagation();
              if (!open) {
                openDropdown();
                setFocusedIndex(filteredOptions.length - 1);
              } else {
                setFocusedIndex((prev) => {
                  const next =
                    prev === null ? filteredOptions.length - 1 : Math.max(prev - 1, 0);
                  return next;
                });
              }
            }
          }}
        />
        <span
          className="input-icon input-icon-select-arrow"
          aria-hidden="true"
        >
          <Icon name="chevron-down" />
        </span>
      </div>

      {open && (
        <div className="input-select__menu input-select__menu--desktop">
          <div className="input-select__menu-list">
            {filteredOptions.map((opt, index) => {
                const optLabel =
                  opt && (opt.question || opt.label)
                    ? opt.question || opt.label
                    : String(opt);
                const key = (opt && (opt.quid || opt.value)) || optLabel;
                const isSelected =
                  selected &&
                  ((selected.quid && opt.quid && selected.quid === opt.quid) ||
                    (selected.value &&
                      opt.value &&
                      selected.value === opt.value));
                return (
                  <div
                    key={key}
                    className={`input-select__option ${
                      isSelected ? "input-select__option--is-selected" : ""
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <button
                      type="button"
                      ref={(el) => (optionRefs.current[index] = el)}
                      className="input-select__option-button"
                      disabled={disabled}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelect(opt, e);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setFocusedIndex((idx) =>
                            Math.min(
                              (idx === null ? index : idx) + 1,
                              filteredOptions.length - 1
                            )
                          );
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          if (index === 0) {
                            setFocusedIndex(null);
                            controlRef.current?.focus();
                          } else {
                            setFocusedIndex((idx) =>
                              Math.max((idx === null ? index : idx) - 1, 0)
                            );
                          }
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSelect(opt, e, false);
                        }
                        if (e.key === " ") {
                          e.preventDefault();
                          handleSelect(opt, e, false);
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          setOpen(false);
                          setFilterText('');
                          controlRef.current?.focus();
                        }
                      }}
                    >
                      {optLabel}
                    </button>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilitySelect;